import { Feature, FeatureCollection } from 'geojson';
import mapboxgl from 'mapbox-gl';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Layer, Map, MapLayerMouseEvent, MapRef, NavigationControl, ScaleControl, Source } from 'react-map-gl';
import styled from 'styled-components';
import { Insights } from '../../utils/interfaces';
import constants, { FLY_TO_DURATION, INSIGHT_ZOOM_LEVEL } from '../constants';

const MapContainer = styled.div<{ isLoadingData: boolean }>`
    width: 100%;
    height: 100%;
    opacity: ${(props) => (props.isLoadingData ? 0.5 : 1)};
`;

export interface ILayer {
    id: string;
    name: string;
    static?: boolean;
    data: FeatureCollection | undefined;
    fill?: unknown;
    stroke?: string;
    filter?: unknown[];
    fillHover?: 'string';
    strokeHover?: 'string';
    type: 'line' | 'circle' | 'fill';
    fillOpacity?: number;
    paint?: unknown;
    popupType?: 'detection' | 'toi';
    circleRadius?: number;
    cluster?: boolean;
}

const RMap: React.FC<{
    insights: Insights | undefined;
    clouds: unknown | undefined;
    customData: unknown | undefined;
    metaData: unknown | undefined;
    centerPosition: [number, number] | null;
    initalZoom: number;
    activeDate: Date | undefined;
    availableDates: Date[] | undefined;
    isInsightsLoading: boolean;
    map: unknown;
    setMap: (map: unknown) => void;
    popupRef: React.MutableRefObject<mapboxgl.Popup>;
    visibleLayers: string[];
    selectedFeature: Feature | null;
    setSelectedFeature: (feature: Feature | null) => void;
    setHoveredFeature: (feature: Feature | null) => void;
    hoveredFeature: Feature | null;
    detectionConfidence: number;
}> = ({
    insights,
    clouds,
    customData,
    isInsightsLoading,
    setMap,
    selectedFeature,
    setSelectedFeature,
    visibleLayers,
    setHoveredFeature,
    hoveredFeature,
    metaData,
    centerPosition,
    initalZoom,
    detectionConfidence,
}) => {
    const hoveredItem = useRef<{ layer_id: string; feature_id: string | number | undefined } | null>(null);
    const viewport = {
        latitude: centerPosition ? centerPosition[0] : 57.33870347771735,
        longitude: centerPosition ? centerPosition[1] : 2.042840975438542,
        zoom: initalZoom,
    };

    const handleMapClick = (event: MapLayerMouseEvent) => {
        if (event.features && event.features.length > 0) {
            const clickedFeature = event.features[0];
            const source = clickedFeature.source;
            const insightFeature =
                insights && source in insights
                    ? (insights[source as keyof Insights] as FeatureCollection)?.features?.find(
                          (feature: Feature) => feature.properties?.uuid === clickedFeature.properties?.uuid
                      )
                    : undefined;
            if (insightFeature) {
                setSelectedFeature(insightFeature);
            } else {
                setSelectedFeature(null);
            }
        }
    };

    useEffect(() => {
        if (selectedFeature) {
            const lat = selectedFeature.properties?.lat;
            const lon = selectedFeature.properties?.lon;
            if (mapRef.current && lat && lon) {
                const bounds = mapRef.current.getBounds();
                if (!(bounds.contains([lon, lat]) && mapRef.current.getZoom() > INSIGHT_ZOOM_LEVEL - 1.5)) {
                    mapRef.current?.flyTo({
                        center: [lon, lat],
                        duration: FLY_TO_DURATION,
                        zoom: Math.max(mapRef.current.getZoom(), INSIGHT_ZOOM_LEVEL),
                    });
                }
            }
        }
    }, [selectedFeature]);

    const onHover = useCallback(
        (event: MapLayerMouseEvent) => {
            if (event.features && event.features.length === 0 && hoveredItem.current !== null) {
                mapRef.current?.setFeatureState(
                    { source: hoveredItem.current.layer_id, id: hoveredItem.current.feature_id },
                    { hover: false }
                );
                hoveredItem.current = null;
                setHoveredFeature(null);
                return;
            }

            if (event.features && event.features.length > 0) {
                if (hoveredItem.current !== null) {
                    mapRef.current?.setFeatureState(
                        { source: hoveredItem.current.layer_id, id: hoveredItem.current.feature_id },
                        { hover: false }
                    );
                }

                hoveredItem.current = {
                    layer_id: event.features[0].layer.id,
                    feature_id: event.features[0].id,
                };
                setHoveredFeature(event.features[0]);
                mapRef.current?.setFeatureState(
                    { source: event.features[0].layer.id, id: event.features[0].id },
                    { hover: true }
                );
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const mapRef = React.useRef<MapRef | null>(null);

    const confidenceFilter: [string, (string | string[])[], number] = useMemo(() => {
        return ['>=', ['to-number', ['get', 'object_class_score']], detectionConfidence / 100];
    }, [detectionConfidence]);

    const connectedVesselsFilter = useMemo(() => {
        const connectedLayers = visibleLayers.filter((item) => ['matched_vessels', 'undetected_ais'].includes(item));

        return [
            'all',
            ['match', ['at', 0, ['get', 'LayerIds']], connectedLayers, true, false],
            ['match', ['at', 1, ['get', 'LayerIds']], connectedLayers, true, false],
        ];
    }, [visibleLayers]);

    useEffect(() => {
        if (mapRef.current && centerPosition) {
            mapRef.current?.flyTo({ center: centerPosition, duration: FLY_TO_DURATION, zoom: initalZoom });
        }
    }, [mapRef, mapRef.current, centerPosition, initalZoom]);
    const layers = useMemo(() => {
        if (!insights) return [];

        const colors = constants.colors.markers;

        // Step 1: Combine matched_vessels and undetected_ais
        const matched = insights.matched_vessels?.features || [];
        const undetected = insights.undetected_ais?.features || [];
        const combinedFeatures = [...matched, ...undetected];

        // Step 2: Group by `mmsi`
        const groupByMMSI = (features: Feature[]) => {
            const groups: Record<string, Feature[]> = {};
            features.forEach((feature) => {
                const mmsi = feature.properties?.mmsi;
                if (mmsi) {
                    if (!groups[mmsi]) groups[mmsi] = [];
                    groups[mmsi].push(feature);
                }
            });
            return groups;
        };

        // Step 3: Create LineStrings from groups
        const createLinesFromGroups = (groups: Record<string, Feature[]>) => {
            const lines: Feature[] = [];

            Object.values(groups).forEach((features) => {
                if (features.length > 1) {
                    // Sort features by timestamp ascending (in order to have the lines drawn the correct way)
                    features.sort((a, b) => {
                        const t1 = new Date(a.properties?.timestamp ?? 0).getTime();
                        const t2 = new Date(b.properties?.timestamp ?? 0).getTime();
                        return t1 - t2;
                    });

                    const coordinates = features
                        .map((f) => f.geometry.coordinates)
                        .filter((coords) => Array.isArray(coords)); // Just in case

                    const matching_labels = features.map((f) => f.properties?.matching_label);

                    const STATUS_MAP: Record<string, string> = {
                        matched: 'matched_vessels',
                        undetected: 'undetected_ais',
                    };

                    const mapStatuses = (statuses: string[]) => {
                        return statuses.map((status) => STATUS_MAP[status]).filter((mapped) => mapped !== undefined);
                    }; // Filter out undefineds if unexpected values exist
                    lines.push({
                        type: 'Feature',
                        geometry: {
                            type: 'LineString',
                            coordinates,
                        },
                        properties: { LayerIds: mapStatuses(matching_labels) },
                    });
                }
            });

            return {
                type: 'FeatureCollection',
                features: lines,
            };
        };

        const groupedByMMSI = groupByMMSI(combinedFeatures);
        const connectedLinesData = createLinesFromGroups(groupedByMMSI);

        const new_layers: ILayer[] = [
            {
                id: 'aoi',
                name: 'aoi',
                type: 'line',
                paint: {
                    'line-color': '#00A1FF',
                },
                data: metaData?.features
                    ? ({ type: 'FeatureCollection', features: metaData.features } as FeatureCollection)
                    : { type: 'FeatureCollection', features: [] },
            },
            {
                id: `custom_data`,
                name: 'custom_data',
                data: customData!,
                paint: { 'fill-color': constants.colors.pallet.lightGreen, 'fill-opacity': 0.3 },
                type: 'fill',
            },
            {
                id: `footprints`,
                name: 'footprints',
                static: true,
                data: insights!.product_footprints,
                paint: {
                    'fill-color': [
                        'match',
                        ['get', 'processing_status'],
                        'completed',
                        '#28A745',
                        'started',
                        '#00A1FF',
                        'waiting',
                        '#FFD700',
                        'failed',
                        '#c72626',
                        '#00A1FF', // Default color in overwatch without status-coloring
                    ],
                    'fill-opacity': 0.3,
                },
                type: 'fill',
            },
            {
                id: `cloudmasks`,
                name: 'cloudmasks',
                static: true,
                data: clouds!,
                paint: { 'fill-color': '#c0e0f3', 'fill-opacity': 0.2 },
                type: 'fill',
            },
            {
                id: `static_objects`,
                name: 'static_objects',
                static: true,
                data: insights!.static_objects,
                paint: {
                    'circle-color': '#000',
                    'circle-stroke-color': '#00A1FF',
                    'circle-stroke-width': 1,
                },
                type: 'circle',
                popupType: 'detection',
            },
            {
                id: `undetected_ais`,
                name: 'undetected_ais',
                data: insights!.undetected_ais,
                paint: {
                    'circle-color': colors.undetectedAis.fill,
                    'circle-stroke-color': colors.undetectedAis.stroke,
                    'circle-stroke-width': 1,
                },
                type: 'circle',
                popupType: 'detection',
                circleRadius: 3,
            },
            {
                id: `dark_vessels`,
                name: 'dark_vessels',
                data: insights!.dark_vessels,
                paint: {
                    'circle-color': colors.darkShips.fill,
                    'circle-stroke-color': colors.darkShips.stroke,
                    'circle-stroke-width': 1,
                },
                type: 'circle',
                popupType: 'detection',
            },
            {
                id: `matched_vessels`,
                name: 'matched_vessels',
                data: insights!.matched_vessels,
                paint: {
                    'circle-color': colors.matchedVessels.fill,
                    'circle-stroke-color': colors.matchedVessels.stroke,
                    'circle-stroke-width': 1,
                },
                type: 'circle',
                popupType: 'detection',
            },
            {
                id: `connected_vessels`,
                name: 'connected_vessels',
                data: connectedLinesData,
                type: 'line',
                paint: {
                    'line-color': colors.matchedVessels.fill,
                    'line-width': 2,
                    'line-opacity': 0.8,
                },
            },
            {
                id: `connected_vessels_arrow`,
                type: 'symbol',
                data: connectedLinesData,
                layout: {
                    'symbol-placement': 'line',
                    'icon-image': 'arrow_right',
                    'icon-size': 0.5,
                },
            },
        ];

        return new_layers;
    }, [insights, clouds, metaData, customData]);

    // Filter layers based on visibleLayers prop
    const filteredLayers = useMemo(() => {
        return layers.filter((layer) => visibleLayers.includes(layer.id));
    }, [layers, visibleLayers]);

    const onMapLoad = useCallback(() => {
        if (!mapRef.current) return;
        mapRef.current.loadImage('/images/icons/ArrowRight.png', (error: unknown, image: unknown) => {
            if (error) {
                throw error;
            }
            if (mapRef.current) {
                mapRef.current.addImage('arrow_right', image);
            }
        });
        setMap(mapRef.current);
    }, [setMap]);

    const seamarkSource = {
        type: 'raster',
        tiles: ['https://t1.openseamap.org/seamark/{z}/{x}/{y}.png'],
        minzoom: 0,
        maxzoom: 18,
    };

    const seamarkLayer = {
        id: 'seamark-layer',
        type: 'raster',
        source: 'seamarks',
        paint: {
            // Optional: tweak opacity or brightness if desired
            'raster-opacity': 1.0,
        },
    };

    return (
        <MapContainer isLoadingData={isInsightsLoading}>
            <Map
                initialViewState={viewport}
                ref={mapRef}
                onLoad={onMapLoad}
                mapStyle="mapbox://styles/vake-torstein/cl4xzn1gs000p14qgz8nqs0ms"
                mapboxAccessToken="pk.eyJ1IjoidmFrZS10b3JzdGVpbiIsImEiOiJjbDR4emJmbW0xdTRpM21tbG1lbnc3b2JrIn0.V7PXI4I2ndH2mxaJrVCnRw"
                interactiveLayerIds={['static_objects', 'undetected_ais', 'dark_vessels', 'matched_vessels'].filter(
                    (id) => visibleLayers.includes(id)
                )} // Intersection of existing interactiveLayerIds and filteredLayers
                onClick={handleMapClick}
                onMouseMove={onHover}
            >
                <ScaleControl />
                <NavigationControl position="bottom-right" />
                {filteredLayers.map((layer) => (
                    <Source key={layer.id} id={layer.id} type="geojson" data={layer.data}>
                        {(() => {
                            if (layer.id === 'dark_vessels') {
                                return <Layer {...layer} filter={confidenceFilter} />;
                            }
                            if (layer.id === 'connected_vessels' || layer.id === 'connected_vessels_arrow') {
                                return <Layer {...layer} filter={connectedVesselsFilter} />;
                            }
                            return <Layer {...layer} />;
                        })()}
                    </Source>
                ))}
                {selectedFeature?.properties && (
                    <Source key="selectedFeature" id="selectedFeature" type="geojson" data={selectedFeature}>
                        <Layer
                            id="selectedFeature"
                            type="circle"
                            paint={{
                                'circle-radius': 12,
                                'circle-stroke-color': '#f0d971',
                                'circle-stroke-width': 2,
                                'circle-opacity': 0.2,
                                'circle-stroke-opacity': 0.4,
                                'circle-color': '#f0d971',
                            }}
                        />
                    </Source>
                )}
                {hoveredFeature && hoveredFeature.properties && (
                    <Source key="hoveredFeature" id="hoveredFeature" type="geojson" data={hoveredFeature}>
                        <Layer
                            id="hoveredFeature"
                            type="circle"
                            paint={{
                                'circle-radius': 12,
                                'circle-stroke-color': '#f0d971',
                                'circle-stroke-width': 2,
                                'circle-opacity': 0.2,
                                'circle-stroke-opacity': 0.4,
                                'circle-color': '#f0d971',
                            }}
                        />
                    </Source>
                )}
                <Source id="seamarks" type="raster" tiles={seamarkSource.tiles}>
                    <Layer {...seamarkLayer} />
                </Source>
            </Map>
        </MapContainer>
    );
};

export default RMap;
